import { captureDatadogError } from "@/lib/analytics-loader";

/**
 * Classification of why `organization` failed the runtime shape check. The
 * literal-`null` case is what we *expect* for the EPD-6938 bug — a TanStack
 * Router context that hasn't populated yet — but the predicate's other
 * negative branches catch genuinely different failure modes (partial API
 * response, drift in `OrgRouteContext` type, malformed cache hydration).
 *
 * Differentiating these in the DD signal is what makes the telemetry
 * actionable: "we got 200 events of `missing_id` on /dashboard" points at a
 * very different root cause than "we got 200 events of `null` on /dashboard".
 */
type OrgContextInvalidReason =
  | "null"
  | "undefined"
  | "not_object"
  | "missing_id"
  | "non_string_id";

function classifyInvalidOrg(organization: unknown): OrgContextInvalidReason {
  if (organization === null) return "null";
  if (organization === undefined) return "undefined";
  if (typeof organization !== "object") return "not_object";
  if (!("id" in organization)) return "missing_id";
  return "non_string_id";
}

/**
 * Module-scoped dedupe set keyed by `{ callsite, pathname, reason, ...extra }`
 * JSON.
 *
 * Render-time guards can fire on every re-render the component performs
 * while organization is null (a few per second under React StrictMode +
 * a parent state churn). Without dedupe the same diagnostic would emit
 * dozens of times per affected page load, drowning out the very signal
 * we added it to capture and inflating DD bill. We dedupe per page load
 * (resets on full reload) so a fresh occurrence still reports.
 *
 * `reason` is part of the key so a callsite that flips between
 * `null` (parent context not yet populated) and `missing_id` (partial
 * cache hydration) still emits both signals — those are the diagnostic
 * the rest of this PR was added to capture.
 *
 * If dispatch fails we drop the key, which lets a subsequent render
 * retry the dispatch — important because a one-time dispatch failure
 * shouldn't suppress the diagnostic permanently for that callsite.
 */
const reportedOrgContextDiagnostics = new Set<string>();

/**
 * Returns true when `organization` is a non-null object carrying `id`.
 *
 * On false, fires a one-off diagnostic event to Datadog RUM tagged with the
 * callsite + URL so the next occurrence tells us *where* and *why* the parent
 * route's beforeLoad guarantee broke (the prior fixes — PR #783, PR #1034 —
 * shipped without this telemetry, which is why we couldn't find the new
 * crash sites after they migrated).
 *
 * Generic predicate: callers pass `organization` typed as their full shape
 * (e.g. `OrgRouteContext["organization"]`). On the truthy branch TS narrows
 * to that same shape, so callers can keep accessing `name`, `slug`,
 * `members`, etc. without recasting.
 *
 * Diagnostic dispatch is fire-and-forget; failures to reach DD must never
 * surface to the user. The return value is what gates the caller's render
 * or beforeLoad redirect.
 */
export function ensureOrgContext<T extends { id: string }>(
  organization: T | null | undefined,
  callsite: string,
  extra?: Record<string, string | number | boolean | null | undefined>,
): organization is T {
  if (
    organization !== null &&
    organization !== undefined &&
    typeof organization === "object" &&
    "id" in organization &&
    typeof organization.id === "string"
  ) {
    return true;
  }

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : null;
  const reason = classifyInvalidOrg(organization);
  const diagnosticKey = JSON.stringify({
    callsite,
    pathname,
    reason,
    ...extra,
  });

  if (!reportedOrgContextDiagnostics.has(diagnosticKey)) {
    reportedOrgContextDiagnostics.add(diagnosticKey);
    void captureDatadogError(
      new Error(`Invalid organization context (${reason}) at ${callsite}`),
      {
        callsite,
        pathname,
        reason,
        ...extra,
      },
    ).catch(() => {
      // Allow retry on subsequent renders if dispatch failed transiently.
      reportedOrgContextDiagnostics.delete(diagnosticKey);
    });
  }

  return false;
}
